import { useId, type ReactNode } from 'react';
import { testId } from '../../app/testId';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './State.module.css';

// Error state — DESIGN_SYSTEM §12 row 29 (`.error-state`) and REQ-287 AC1 / AC3: retryable · permission ·
// offline. Says what happened and whether ads / spend are affected, offers Retry, and shows a small
// reference id — never raw exception text or a status code (the words come from `error.<code>` messages
// through props). Test id `state.error` by default; the retry action is the caller's button.

export interface ErrorStateProps {
  icon?: IconName;
  title: string;
  /** One or two sentences: what happened, whether ads are affected, what next. */
  body?: ReactNode;
  /** Usually one secondary "Try again" button. */
  action?: ReactNode;
  /** Small muted line under the actions ("Last updated 9:12am"). */
  detail?: ReactNode;
  /** The support reference ("7f3a-21c9"), rendered in monospace after `detail`. */
  reference?: string;
  compact?: boolean;
  testID?: string;
  className?: string;
}

export function ErrorState({
  icon = 'cloud-off',
  title,
  body,
  action,
  detail,
  reference,
  compact = false,
  testID = 'state.error',
  className,
}: ErrorStateProps) {
  const titleId = useId();
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className={cx(styles.state, styles.errorState, compact && styles.compact, className)}
      data-testid={testID}
    >
      <span className={cx(styles.art, styles.artError)}>
        <Icon name={icon} size={24} />
      </span>
      <h3 id={titleId} className={styles.title}>
        {title}
      </h3>
      {body ? <p className={styles.body}>{body}</p> : null}
      {action ? <div className={styles.actions}>{action}</div> : null}
      {detail || reference ? (
        <span className={styles.detail}>
          {detail}
          {detail && reference ? ' · ' : null}
          {reference ? (
            <span className={styles.mono} data-testid={testId(testID, 'reference')}>
              ref {reference}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
